import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DatabaseIcon, HeartHandshakeIcon, TimerIcon, TagIcon, ShieldCheckIcon, TrendingUpIcon } from 'lucide-react';
export function AdvantagesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-navy-50/50" data-id="element-22">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-23">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-24">
          {/* Left: text */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-25">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-26">
              Чому ми
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-27">
              Наші переваги
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-28">
              Ми не просто закриваємо вакансії — ми будуємо команди, які рухають
              бізнес уперед
            </p>

            {/* Decorative element */}
            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 text-white" data-id="element-29">
              <div className="text-3xl font-extrabold" data-id="element-30">98%</div>
              <div className="mt-1 text-primary-100 text-sm font-medium" data-id="element-31">
                кандидатів проходять випробувальний термін
              </div>
            </div>
          </motion.div>

          {/* Right: advantages list */}
          <div className="space-y-5" data-id="element-32">
            {/* Advantage 1 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-33">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center" data-id="element-34">
                <DatabaseIcon className="w-6 h-6" data-id="element-35" />
              </div>
              <div data-id="element-36">
                <h3 className="font-bold text-navy-900" data-id="element-37">
                  Широка база кандидатів
                </h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-38">
                  Понад 100 000 перевірених спеціалістів у нашій базі даних за
                  всіма напрямками
                </p>
              </div>
            </motion.div>

            {/* Advantage 2 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-39">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-40">
                <HeartHandshakeIcon className="w-6 h-6" data-id="element-41" />
              </div>
              <div data-id="element-42">
                <h3 className="font-bold text-navy-900" data-id="element-43">Персональний підхід</h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-44">
                  Персональний рекрутер для кожного проєкту, глибоке занурення у
                  специфіку бізнесу
                </p>
              </div>
            </motion.div>

            {/* Advantage 3 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-45">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-46">
                <TimerIcon className="w-6 h-6" data-id="element-47" />
              </div>
              <div data-id="element-48">
                <h3 className="font-bold text-navy-900" data-id="element-49">
                  Економія часу та витрат
                </h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-50">
                  Середній термін закриття — 14 днів. Ви економите ресурси на
                  пошук та відбір
                </p>
              </div>
            </motion.div>

            {/* Advantage 4 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-51">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-52">
                <TagIcon className="w-6 h-6" data-id="element-53" />
              </div>
              <div data-id="element-54">
                <h3 className="font-bold text-navy-900" data-id="element-55">Прозорі ціни</h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-56">
                  Фіксована вартість без прихованих платежів. Оплата за фактом
                  виходу кандидата
                </p>
              </div>
            </motion.div>

            {/* Advantage 5 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-57">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-58">
                <ShieldCheckIcon className="w-6 h-6" data-id="element-59" />
              </div>
              <div data-id="element-60">
                <h3 className="font-bold text-navy-900" data-id="element-61">Гарантія заміни</h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-62">
                  90 днів гарантійного періоду. Якщо кандидат не підійшов —
                  підберемо заміну безкоштовно
                </p>
              </div>
            </motion.div>

            {/* Advantage 6 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.6
          }} className="flex gap-4 p-5 rounded-xl bg-white border border-navy-100" data-id="element-63">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center" data-id="element-64">
                <TrendingUpIcon className="w-6 h-6" data-id="element-65" />
              </div>
              <div data-id="element-66">
                <h3 className="font-bold text-navy-900" data-id="element-67">
                  Аналітика та звітність
                </h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-68">
                  Щотижневі звіти про хід підбору, аналітика ринку та
                  рекомендації щодо умов
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}